import { useState } from 'react';
import Logo from '../components/Logo';

// ── Design tokens ─────────────────────────────────────────────
const C = {
  bg:       '#0f1117',
  card:     '#1e2329',
  border:   '#2a3040',
  text:     '#f0f2f5',
  muted:    '#6b7280',
  teal:     '#14b8a6',
  tealDim:  'rgba(20,184,166,0.12)',
  amber:    '#f59e0b',
  red:      '#ef4444',
  green:    '#86efac',
  blue:     '#93c5fd',
};
const F = 'system-ui, -apple-system, sans-serif';

// ── Mock data ─────────────────────────────────────────────────
const JOBS = [
  {
    id: 1, status: 'running', started_at: new Date(Date.now() - 8.4 * 3600000).toISOString(),
    setter_name: 'M. Kovač', target_cycle_s: 28, good_parts_count: 0,
    parts:   { name: 'Front Cover A34', weight_log_interval_minutes: 60 },
    moulds:  { ref: 'T-0042', name: 'Front Cover 4cav', cavities: 4 },
    machines:{ name: 'KM 280', tonnes: 280 },
    recipe:  { name: 'PP+30GF Natural', material: { name: 'PP+30GF', colour: null } },
    target_parts: 12456, order_number: 'ORD-2024-881',
    weights: [{ weight_g: 48.2 }, { weight_g: 48.5 }, { weight_g: 47.9 }],
    target_weight_g: 48.0, scrap_count: 3, inspections: { pass: 4, fail: 0 },
    planTotals: { total_good_parts: 4114, target_parts: 12456, total_shifts: 3, completed_shifts: 1 },
  },
  {
    id: 2, status: 'running', started_at: new Date(Date.now() - 1.2 * 3600000).toISOString(),
    setter_name: 'J. Novak', target_cycle_s: 42, good_parts_count: 0,
    parts:   { name: 'Lid Assembly B07', weight_log_interval_minutes: 60 },
    moulds:  { ref: 'T-0019', name: 'Lid 2cav', cavities: 2 },
    machines:{ name: 'KM 180', tonnes: 180 },
    recipe:  { name: 'ABS Black Flame Ret.', material: { name: 'ABS', colour: '#1a1a1a' } },
    target_parts: 3200, order_number: 'ORD-2024-904',
    weights: [{ weight_g: 31.1 }],
    target_weight_g: 31.0, scrap_count: 0, inspections: { pass: 1, fail: 0 },
    planTotals: null,
  },
];


const OPT_PARAMS = {
  zones: [{ label: 'Z1 Feed', temp: 220 }, { label: 'Z2', temp: 230 }, { label: 'Z3', temp: 238 }, { label: 'Z4 / Nozzle', temp: 242 }],
  mould_temp: 60, melt_temp_estimated: 248, fill_time_s: 1.4,
  inj_pressure_bar: 1240, inj_stages: [{ speed_pct: 75, pressure_bar: 1600 }, { speed_pct: 45, pressure_bar: 1400 }, { speed_pct: 20, pressure_bar: 1200 }],
  hold_stages: [{ pressure_bar: 760, time_s: 4.0 }, { pressure_bar: 580, time_s: 2.5 }, { pressure_bar: 320, time_s: 1.5 }],
  cooling_min_s: 18.4, cooling_rec_s: 22.1, screw_speed_rpm: 148, back_pressure_bar: 62,
  recovery_time_s: 19.8, gate_freeze_s: 5.8, hold_total_s: 8.0,
  checks: {
    melt_in_window:      { pass: true,  value: '248°C',   range: '230–260°C' },
    gate_shear_rate:     { pass: true,  value: '18 400 s⁻¹', range: '< 50 000 s⁻¹' },
    clamp_force:         { pass: true,  value: '224 T',   range: '< 280 T' },
    hold_vs_gate_freeze: { pass: false, value: '8.0 s',   range: '> 6.4 s' },
    recovery_vs_cooling: { pass: true,  value: '19.8 s',  range: '< 22.1 s' },
    residence_time:      { pass: true,  value: '186 s',   range: '< 360 s' },
    shot_barrel_ratio:   { pass: true,  value: '54%',     range: '20–80%' },
  },
};

// ── Helpers ───────────────────────────────────────────────────
function elapsed(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const h  = Math.floor(ms / 3600000);
  const m  = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function Badge({ children, bg, color, border }) {
  return (
    <span style={{ background: bg || 'rgba(255,255,255,0.06)', color: color || C.muted, border: `1px solid ${border || C.border}`, borderRadius: '4px', padding: '2px 7px', fontSize: '10px', fontWeight: '700' }}>
      {children}
    </span>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <div style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: '700', color: color || C.text }}>{value}</div>
    </div>
  );
}

function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, Math.round(value / max * 100)) : 0;
  return (
    <div style={{ background: C.bg, borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
      <div style={{ height: '100%', borderRadius: '4px', background: color || C.teal, width: `${pct}%`, transition: 'width 0.3s' }} />
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: C.muted, marginBottom: '16px', paddingBottom: '8px', borderBottom: `1px solid ${C.border}` }}>
      {children}
    </div>
  );
}

function Btn({ children, color, bg, border, glow }) {
  return (
    <button style={{
      background: bg || C.bg, border: `1px solid ${border || C.teal}`, color: color || C.teal,
      borderRadius: '7px', padding: '9px 10px', fontSize: '12px', fontWeight: '700',
      cursor: 'pointer', fontFamily: F, flex: 1,
      animation: glow ? 'optimise-glow 1.8s ease-in-out infinite' : undefined,
    }}>
      {children}
    </button>
  );
}

// ── Nav replica ───────────────────────────────────────────────
function NavBar({ active }) {
  const links = [
    { path: 'today', label: 'Today', icon: 'fa-solid fa-clock' },
    { path: 'parts', label: 'Parts', icon: 'fa-solid fa-cube' },
    { path: 'process', label: 'Process', icon: 'fa-solid fa-sliders' },
    { path: 'admin', label: 'Admin', icon: 'fa-solid fa-gear' },
    { path: 'faq', label: 'FAQ', icon: 'fa-solid fa-circle-question' },
  ];
  return (
    <nav style={{ background: '#1e2329', borderBottom: `1px solid ${C.border}`, padding: '0 16px', display: 'flex', alignItems: 'center', height: '48px', fontFamily: F }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: '16px', borderRight: `1px solid ${C.border}`, marginRight: '16px' }}><Logo size={15} /></div>
      <div style={{ display: 'flex', flex: 1, gap: 0 }}>
        {links.map(l => (
          <div key={l.path} style={{ fontSize: '12px', fontWeight: '500', padding: '0 14px', height: '48px', display: 'flex', alignItems: 'center', color: active === l.path ? C.text : C.muted, borderBottom: active === l.path ? `2px solid ${C.teal}` : '2px solid transparent', gap: '6px', cursor: 'pointer' }}>
            <i className={l.icon} style={{ fontSize: '13px', color: active === l.path ? C.teal : C.muted }} />
            {l.label}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: C.text }}>Matej Golob</div>
          <div style={{ fontSize: '10px', color: C.muted, background: C.border, padding: '2px 8px', borderRadius: '8px', marginTop: '1px' }}>director</div>
        </div>
        <button style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer' }}>Sign out</button>
      </div>
    </nav>
  );
}

// ── Preview section nav ───────────────────────────────────────
const SECTIONS = [
  { key: 'today',    label: 'Today — Running Jobs' },
  { key: 'schedule', label: 'Admin / Schedule' },
  { key: 'live',     label: 'Live Job Panel' },
  { key: 'modals',   label: 'Modals & Prompts' },
  { key: 'optim',    label: 'Process / Optimisation' },
];

function SectionNav({ active, setActive }) {
  return (
    <div style={{ background: '#161b22', borderBottom: `1px solid ${C.border}`, padding: '0 20px', display: 'flex', gap: '4px', overflowX: 'auto', position: 'sticky', top: 0, zIndex: 49 }}>
      {SECTIONS.map(s => (
        <button key={s.key} onClick={() => setActive(s.key)} style={{
          background: 'none', border: 'none', borderBottom: active === s.key ? `2px solid ${C.amber}` : '2px solid transparent',
          color: active === s.key ? C.amber : C.muted, padding: '10px 14px', fontSize: '12px', fontWeight: '600',
          cursor: 'pointer', fontFamily: F, whiteSpace: 'nowrap',
        }}>
          {s.label}
        </button>
      ))}
      <div style={{ marginLeft: 'auto', padding: '8px 0', display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: '10px', color: C.muted, background: 'rgba(245,158,11,0.1)', border: `1px solid rgba(245,158,11,0.3)`, borderRadius: '5px', padding: '3px 10px' }}>
          ⚠ Preview mode — read only, mock data
        </span>
      </div>
    </div>
  );
}

// ── TODAY ─────────────────────────────────────────────────────
function RunningCard({ job }) {
  const lastW    = job.weights[job.weights.length - 1];
  const drift    = lastW ? +(((lastW.weight_g - job.target_weight_g) / job.target_weight_g) * 100).toFixed(1) : null;
  const driftColor = drift == null ? C.muted : Math.abs(drift) <= 2 ? C.green : Math.abs(drift) <= 5 ? C.amber : C.red;
  const cavities = job.moulds.cavities || 1;
  const shiftMs  = Date.now() - new Date(job.started_at).getTime();
  const expected = job.target_cycle_s ? Math.floor(shiftMs / 1000 / job.target_cycle_s * cavities) : null;
  const pt       = job.planTotals;
  const isMulti  = pt?.total_shifts > 1;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden', fontFamily: F }}>
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: C.text, marginBottom: '2px' }}>{job.parts.name}</div>
            <div style={{ fontSize: '11px', color: C.muted }}>{job.moulds.ref} · {job.machines.name} · {job.recipe.name}</div>
          </div>
          <Badge bg="rgba(20,184,166,0.1)" color={C.teal} border="rgba(20,184,166,0.3)">● {elapsed(job.started_at)}</Badge>
        </div>
        <div style={{ fontSize: '11px', color: C.muted, marginTop: '4px' }}>Setter: {job.setter_name} · {job.moulds.cavities} cavities · Order: {job.order_number}</div>
      </div>

      {/* Order progress */}
      {pt && (
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: 'rgba(20,184,166,0.04)' }}>
          <div style={{ fontSize: '10px', color: C.muted, marginBottom: '4px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Order Progress {isMulti && `· ${pt.completed_shifts} of ${pt.total_shifts} shifts`}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '14px', fontWeight: '800', color: C.text }}>{pt.total_good_parts.toLocaleString()} <span style={{ fontSize: '11px', color: C.muted, fontWeight: '400' }}>/ {pt.target_parts.toLocaleString()} pcs</span></span>
            <span style={{ fontSize: '10px', color: C.muted }}>{(pt.target_parts - pt.total_good_parts).toLocaleString()} remaining</span>
          </div>
          <ProgressBar value={pt.total_good_parts} max={pt.target_parts} />
          {expected != null && <div style={{ fontSize: '10px', color: C.muted, marginTop: '5px' }}>Expected today: ~{expected.toLocaleString()} pcs</div>}
        </div>
      )}

      {/* Stats */}
      <div style={{ padding: '10px 14px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', borderBottom: `1px solid ${C.border}` }}>
        <Stat label="Weight" value={lastW ? `${Number(lastW.weight_g).toLocaleString()}g` : '—'} color={C.text} />
        <Stat label="Drift" value={drift != null ? `${drift > 0 ? '+' : ''}${drift}%` : '—'} color={driftColor} />
        <Stat label="Scrap" value={`${job.scrap_count} pcs`} color={job.scrap_count > 0 ? C.red : C.green} />
        <Stat label="Inspections" value={`${job.inspections.pass}✓ ${job.inspections.fail}✗`} color={job.inspections.fail > 0 ? C.amber : C.green} />
      </div>

      {/* Actions */}
      <div style={{ padding: '10px 14px', display: 'flex', gap: '6px' }}>
        <Btn>⚖ Log Weight</Btn>
        <Btn>🔍 Inspect</Btn>
        <Btn>⚠ Scrap</Btn>
        <Btn color="#fca5a5" border="rgba(239,68,68,0.4)">■ End Job</Btn>
      </div>
    </div>
  );
}

function TodayPreview() {
  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '40px 16px 40px' }}>
      <NavBar active="today" />
      <div style={{ height: '16px' }} />

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
        {['Today', 'This Week', 'All Time'].map((t, i) => (
          <button key={t} style={{ background: i === 0 ? C.tealDim : 'none', border: `1px solid ${i === 0 ? C.teal : C.border}`, color: i === 0 ? C.teal : C.muted, borderRadius: '6px', padding: '5px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: F }}>{t}</button>
        ))}
      </div>

      <SectionLabel>Currently Running — 2 jobs</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
        {JOBS.map(j => <RunningCard key={j.id} job={j} />)}
      </div>

      <SectionLabel>Scheduled for Today</SectionLabel>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>Housing Shell E03</div>
          <div style={{ fontSize: '11px', color: C.muted }}>KM 350 · PP Natural · 6 500 pcs</div>
        </div>
        <button style={{ background: C.tealDim, border: `1px solid ${C.teal}`, color: C.teal, borderRadius: '7px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: F }}>▶ Start</button>
      </div>

      <SectionLabel>Completed Today</SectionLabel>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', opacity: 0.8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>Cap Ring C11</div>
            <div style={{ fontSize: '11px', color: C.muted }}>KM 180 · Ended 07:42 · End of shift</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: C.green }}>8 000 pcs ✓</div>
            <div style={{ fontSize: '10px', color: C.muted }}>Golden run ⭐</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SCHEDULE ──────────────────────────────────────────────────
function SchedulePreview() {
  const days = ['Mon 19', 'Tue 20', 'Wed 21', 'Thu 22', 'Fri 23', 'Sat 24', 'Sun 25'];
  const machines = ['KM 280', 'KM 180', 'KM 350', 'KM 500'];
  const mockCells = {
    'KM 280-Mon 19': { status: 'started',   part: 'Front Cover A34',   order: 'ORD-881', color: '#1d4ed8' },
    'KM 280-Tue 20': { status: 'scheduled', part: 'Front Cover A34',   order: 'ORD-881', color: '#1d4ed8' },
    'KM 280-Wed 21': { status: 'scheduled', part: 'Front Cover A34',   order: 'ORD-881', color: '#1d4ed8' },
    'KM 280-Thu 22': { status: 'scheduled', part: 'Bracket D22',       order: 'ORD-892', color: null },
    'KM 180-Mon 19': { status: 'started',   part: 'Lid Assembly B07',  order: 'ORD-904', color: '#1a1a1a' },
    'KM 180-Fri 23': { status: 'scheduled', part: 'Housing Shell E03', order: 'ORD-915', color: '#6d28d9' },
    'KM 350-Tue 20': { status: 'scheduled', part: 'Base Plate B12',    order: 'ORD-898', color: '#065f46' },
    'KM 350-Wed 21': { status: 'scheduled', part: 'Base Plate B12',    order: 'ORD-898', color: '#065f46' },
    'KM 500-Thu 22': { status: 'scheduled', part: 'Tank Body F01',     order: 'ORD-921', color: '#7c2d12' },
    'KM 500-Fri 23': { status: 'scheduled', part: 'Tank Body F01',     order: 'ORD-921', color: '#7c2d12' },
  };
  const statusColor = { started: { bg: 'rgba(20,184,166,0.1)', border: 'rgba(20,184,166,0.35)', text: C.teal }, scheduled: { bg: 'rgba(255,255,255,0.04)', border: C.border, text: C.muted }, completed: { bg: 'rgba(134,239,172,0.08)', border: 'rgba(134,239,172,0.2)', text: C.green } };

  return (
    <div style={{ padding: '40px 16px', fontFamily: F }}>
      <NavBar active="admin" />
      <div style={{ height: '16px' }} />
      {/* Sub-nav */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['Machines', 'Moulds', 'Materials', 'Schedule', 'Users'].map((t, i) => (
          <button key={t} style={{ background: i === 3 ? C.tealDim : 'none', border: `1px solid ${i === 3 ? C.teal : C.border}`, color: i === 3 ? C.teal : C.muted, borderRadius: '6px', padding: '5px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: F }}>{t}</button>
        ))}
      </div>

      {/* View controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {['Week', 'Day', 'Month'].map((m, i) => (
            <button key={m} style={{ background: i === 0 ? C.tealDim : 'none', border: `1px solid ${i === 0 ? C.teal : C.border}`, color: i === 0 ? C.teal : C.muted, borderRadius: '6px', padding: '4px 12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: F }}>{m}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>‹</button>
          <span style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>19 May — 25 May 2026</span>
          <button style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>›</button>
        </div>
        <button style={{ background: C.tealDim, border: `1px solid ${C.teal}`, color: C.teal, borderRadius: '7px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: F }}>+ Schedule Job</button>
      </div>

      {/* Grid */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '100px repeat(7, minmax(110px,1fr))', gap: '3px', minWidth: '900px' }}>
          <div style={{ background: '#161b22', borderRadius: '5px', padding: '6px' }} />
          {days.map((d, i) => (
            <div key={d} style={{ background: i === 0 ? 'rgba(20,184,166,0.1)' : '#161b22', border: i === 0 ? '1px solid rgba(20,184,166,0.3)' : `1px solid ${C.border}`, borderRadius: '5px', padding: '6px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: i === 0 ? C.teal : C.text }}>{d}</div>
              <div style={{ fontSize: '9px', color: C.muted }}>May 2026</div>
            </div>
          ))}
          {machines.map(machine => (
            <>
              <div key={machine} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '5px', padding: '6px 8px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: C.text }}>{machine}</div>
                <div style={{ fontSize: '9px', color: C.muted }}>{machine.includes('500') ? '500T' : machine.includes('350') ? '350T' : machine.includes('280') ? '280T' : '180T'}</div>
              </div>
              {days.map((d, di) => {
                const cell = mockCells[`${machine}-${d}`];
                const sc   = statusColor[cell?.status] || statusColor.scheduled;
                return (
                  <div key={`${machine}-${d}`} style={{ background: cell ? (di === 0 && cell.status === 'started' ? 'rgba(20,184,166,0.08)' : '#1e2329') : '#161b22', border: `1px solid ${cell ? (di === 0 && cell.status === 'started' ? 'rgba(20,184,166,0.25)' : C.border) : C.border}`, borderRadius: '5px', padding: cell ? '4px' : '3px', minHeight: '52px' }}>
                    {cell && (
                      <div style={{ background: cell.color ? `${cell.color}22` : sc.bg, border: `1px solid ${cell.color ? `${cell.color}55` : sc.border}`, borderRadius: '4px', padding: '4px 6px' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell.part}</div>
                        <div style={{ fontSize: '8px', color: cell.color ? C.muted : sc.text, marginTop: '2px' }}>{cell.status}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── LIVE PANEL ────────────────────────────────────────────────
function LivePanelPreview() {
  const job = JOBS[0];
  const pt  = job.planTotals;
  const totalMade   = pt.total_good_parts;
  const orderTarget = pt.target_parts;
  const pct         = Math.round(totalMade / orderTarget * 100);

  return (
    <div style={{ display: 'flex', fontFamily: F, padding: '40px 16px', gap: '20px' }}>
      {/* Background context */}
      <div style={{ flex: 1, opacity: 0.3 }}>
        <NavBar active="admin" />
        <div style={{ padding: '20px', color: C.muted, fontSize: '12px' }}>← Schedule view in background</div>
      </div>

      {/* Panel */}
      <div style={{ width: 'min(460px, 100vw)', background: C.card, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', fontFamily: F, flexShrink: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700' }}>Live Job</div>
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>{job.parts.name} · {job.machines.name}</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: C.muted, fontSize: '18px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' }}>
          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <Stat label="Status" value="Running" />
            <Stat label="Elapsed" value={elapsed(job.started_at)} />
            <Stat label="Setter" value={job.setter_name} />
            <Stat label="Recipe" value={job.recipe.name} />
          </div>

          {/* Order Progress hero */}
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.08em', marginBottom: '10px' }}>
              Order Progress
              <span style={{ marginLeft: '8px', fontWeight: '400', textTransform: 'none', fontSize: '11px' }}>· {pt.completed_shifts} of {pt.total_shifts} shifts done</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '10px' }}>
              <span style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-0.02em', lineHeight: 1 }}>{totalMade.toLocaleString()}</span>
              <span style={{ fontSize: '15px', color: C.muted }}>/ {orderTarget.toLocaleString()} pcs</span>
            </div>
            <div style={{ background: '#1e2329', borderRadius: '4px', height: '10px', overflow: 'hidden', marginBottom: '8px' }}>
              <div style={{ height: '100%', borderRadius: '4px', background: C.teal, width: `${pct}%` }} />
            </div>
            <div style={{ fontSize: '11px', color: C.muted }}>{(orderTarget - totalMade).toLocaleString()} pcs remaining</div>
          </div>

          {/* This Shift */}
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.08em', marginBottom: '12px' }}>This Shift</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '10px' }}>
              <span style={{ color: C.muted }}>Finished pcs</span>
              <span style={{ fontWeight: '700' }}>0 / 4 114</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Stat label="Scrap" value="3 pcs" color={C.red} />
              <Stat label="Scrap rate" value="—" />
              <Stat label="Pace (pcs/h)" value="—" />
              <Stat label="Required pace" value="493" />
            </div>
          </div>

          {/* Shot Weight */}
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.08em', marginBottom: '12px' }}>Shot Weight</div>
            {/* Mini chart placeholder */}
            <div style={{ height: '80px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${C.border}` }}>
              <span style={{ fontSize: '10px', color: C.muted }}>Weight trend chart</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Stat label="Latest" value="48.2g" />
              <Stat label="Target" value="48.0g" />
              <Stat label="Drift" value="+0.4%" color={C.green} />
              <Stat label="Last logged" value="12m ago" color={C.green} />
            </div>
          </div>

          {/* Quality */}
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.08em', marginBottom: '12px' }}>Quality</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Stat label="Inspections passed" value="4 / 4" />
              <Stat label="Pass rate" value="100%" color={C.green} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MODALS ────────────────────────────────────────────────────
function ModalWrapper({ title, sub, children, wide }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', width: wide ? '460px' : '380px', fontFamily: F, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: '15px', fontWeight: '700' }}>{title}</div>
        {sub && <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function Input({ label, placeholder, value, type }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {label && <label style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted }}>{label}</label>}
      <input readOnly value={value || ''} placeholder={placeholder} type={type || 'text'} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '6px', color: C.text, padding: '10px 12px', fontSize: '14px', fontFamily: F }} />
    </div>
  );
}

function ModalsPreview() {
  return (
    <div style={{ padding: '40px 16px', fontFamily: F }}>
      <SectionLabel>Setter name prompt — clicking Start on a scheduled job</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '36px' }}>
        <ModalWrapper title="Start Job" sub="Front Cover A34 · KM 280">
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Input label="Setter name" placeholder="Who is setting up this job?" />
          </div>
          <div style={{ display: 'flex', gap: '8px', padding: '0 20px 20px' }}>
            <Btn color={C.muted} border={C.border}>Cancel</Btn>
            <Btn>▶ Start</Btn>
          </div>
        </ModalWrapper>
      </div>

      <SectionLabel>End Job modal</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '36px' }}>
        <ModalWrapper title="End Job" sub="Front Cover A34 — 8h 24m" wide>
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Order context box */}
            <div style={{ background: 'rgba(20,184,166,0.06)', border: `1px solid rgba(20,184,166,0.2)`, borderRadius: '8px', padding: '10px 14px', fontSize: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}><span style={{ color: C.muted }}>Order target</span><span style={{ fontWeight: '700' }}>12 456 pcs</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}><span style={{ color: C.muted }}>Made in previous shifts</span><span style={{ fontWeight: '700', color: C.teal }}>4 114 pcs</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}><span style={{ color: C.muted }}>Remaining before this shift</span><span style={{ fontWeight: '700' }}>8 342 pcs</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: C.muted }}>Expected today</span><span style={{ fontWeight: '700', color: C.muted }}>~4 114 pcs</span></div>
            </div>
            <Input label="Good parts made" placeholder="e.g. 4 052" type="number" />
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.06em', marginBottom: '6px' }}>End reason</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {['End of shift', 'Machine fault', 'Mould fault', 'Quality issue', 'Material issue'].map((r, i) => (
                  <button key={r} style={{ background: i === 0 ? C.tealDim : 'none', border: `1px solid ${i === 0 ? C.teal : C.border}`, color: i === 0 ? C.teal : C.muted, borderRadius: '6px', padding: '5px 12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: F }}>{r}</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" readOnly />
              <span style={{ fontSize: '12px', color: C.muted }}>Mark as golden run ⭐</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', padding: '0 20px 20px' }}>
            <Btn color={C.muted} border={C.border}>Cancel</Btn>
            <Btn color="#fca5a5" border="rgba(239,68,68,0.4)">■ End Job</Btn>
          </div>
        </ModalWrapper>
      </div>

      <SectionLabel>Log Weight modal</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '36px' }}>
        <ModalWrapper title="Log Shot Weight" sub="Front Cover A34 · Target: 48.0g">
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
              {[48.2, 48.5, 47.9].map((w, i) => (
                <div key={i} style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: C.text }}>{w}g</div>
                  <div style={{ fontSize: '9px', color: C.muted }}>prev {i + 1}</div>
                </div>
              ))}
            </div>
            <Input label="Weight (g)" placeholder="48.0" type="number" />
            <div style={{ fontSize: '12px', color: C.muted }}>Drift: <span style={{ color: C.green, fontWeight: '700' }}>+0.4%</span></div>
          </div>
          <div style={{ display: 'flex', gap: '8px', padding: '0 20px 20px' }}>
            <Btn color={C.muted} border={C.border}>Cancel</Btn>
            <Btn>Log Weight</Btn>
          </div>
        </ModalWrapper>
      </div>

      <SectionLabel>Inspection modal</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '36px' }}>
        <ModalWrapper title="Log Inspection" sub="Front Cover A34">
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ flex: 1, background: 'rgba(20,184,166,0.12)', border: `2px solid ${C.teal}`, color: C.teal, borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>✓ Pass</button>
              <button style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>✗ Fail</button>
            </div>
            <div style={{ background: C.bg, border: `2px dashed ${C.border}`, borderRadius: '8px', padding: '16px', textAlign: 'center', color: C.muted, fontSize: '12px' }}>📷 Tap to attach photo</div>
            <Input label="Notes (optional)" placeholder="Any observations..." />
          </div>
          <div style={{ display: 'flex', gap: '8px', padding: '0 20px 20px' }}>
            <Btn color={C.muted} border={C.border}>Cancel</Btn>
            <Btn>Submit</Btn>
          </div>
        </ModalWrapper>
      </div>

      <SectionLabel>Scrap log modal</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        <ModalWrapper title="Log Scrap" sub="Front Cover A34">
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Input label="Quantity" placeholder="e.g. 2" type="number" />
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.06em', marginBottom: '6px' }}>Defect type</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {['Short shot', 'Flash', 'Sink mark', 'Weld line', 'Warpage', 'Burn mark', 'Colour', 'Other'].map((d, i) => (
                  <button key={d} style={{ background: i === 0 ? C.tealDim : 'none', border: `1px solid ${i === 0 ? C.teal : C.border}`, color: i === 0 ? C.teal : C.muted, borderRadius: '5px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', fontFamily: F }}>{d}</button>
                ))}
              </div>
            </div>
            <div style={{ background: C.bg, border: `2px dashed ${C.border}`, borderRadius: '8px', padding: '12px', textAlign: 'center', color: C.muted, fontSize: '12px' }}>📷 Tap to attach photo</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', padding: '0 20px 20px' }}>
            <Btn color={C.muted} border={C.border}>Cancel</Btn>
            <Btn color="#fca5a5" border="rgba(239,68,68,0.4)">Log Scrap</Btn>
          </div>
        </ModalWrapper>
      </div>
    </div>
  );
}

// ── OPTIMISATION ──────────────────────────────────────────────
function OptimisationPreview() {
  const checks = OPT_PARAMS.checks;
  const checkList = [
    { key: 'melt_in_window',      label: 'Melt in window',        sev: 'amber' },
    { key: 'gate_shear_rate',     label: 'Gate shear rate',       sev: 'red'   },
    { key: 'clamp_force',         label: 'Clamp force',           sev: 'red'   },
    { key: 'hold_vs_gate_freeze', label: 'Hold vs gate freeze',   sev: 'amber' },
    { key: 'recovery_vs_cooling', label: 'Recovery vs cooling',   sev: 'amber' },
    { key: 'residence_time',      label: 'Residence time',        sev: 'red'   },
    { key: 'shot_barrel_ratio',   label: 'Shot / barrel ratio',   sev: 'amber' },
  ];

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 16px', fontFamily: F }}>
      <NavBar active="process" />
      <div style={{ height: '16px' }} />
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '800', color: C.text }}>Front Cover A34 — T-0042</div>
          <div style={{ fontSize: '12px', color: C.muted, marginTop: '4px' }}>KM 280 · PP+30GF Natural · 4 cavities</div>
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Btn color={C.muted} border={C.border}>⭐ Mark Golden</Btn>
          <Btn color={C.blue} bg="#0d1f3a" border="#1d4ed8" glow>✨ Optimise</Btn>
          <Btn color={C.muted} border={C.border}>🔍 Troubleshoot</Btn>
          <Btn color={C.text} border={C.border}>🔧 Suggest Fixes (2)</Btn>
          <Btn color={C.muted} border={C.border}>🕓 History (v4)</Btn>
        </div>
      </div>

      {/* Status banner */}
      <div style={{ background: '#2d1f00', border: '1px solid #92400e', borderRadius: '10px', padding: '12px 18px', marginBottom: '16px' }}>
        <span style={{ fontSize: '15px', fontWeight: '700', color: C.amber }}>⚠ Issues found — review checks below</span>
      </div>

      {/* Checks grid */}
      <SectionLabel>Safety Checks</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: '8px', marginBottom: '24px' }}>
        {checkList.map(({ key, label, sev }) => {
          const check = checks[key];
          const pass  = check?.pass;
          const bg    = pass ? 'rgba(20,184,166,0.06)' : sev === 'red' ? '#3b0f0f' : '#2d1f00';
          const bdr   = pass ? 'rgba(20,184,166,0.2)' : sev === 'red' ? '#991b1b' : '#92400e';
          const icon  = pass ? '✓' : sev === 'red' ? '✗' : '⚠';
          const iconC = pass ? C.green : sev === 'red' ? C.red : C.amber;
          return (
            <div key={key} style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '16px', color: iconC, flexShrink: 0 }}>{icon}</span>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: C.text }}>{label}</div>
                {check && <div style={{ fontSize: '10px', color: C.muted, marginTop: '1px' }}>{check.value} · {check.range}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Parameters — two columns */}
      <SectionLabel>Current Parameters (v4)</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
        {/* Temperatures */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.08em', marginBottom: '12px' }}>Temperatures</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '6px', marginBottom: '10px' }}>
            {OPT_PARAMS.zones.map(z => (
              <div key={z.label} style={{ background: C.bg, borderRadius: '6px', padding: '8px 6px', textAlign: 'center' }}>
                <div style={{ fontSize: '9px', color: C.muted, marginBottom: '3px' }}>{z.label}</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: C.amber }}>{z.temp}°C</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', borderTop: `1px solid ${C.border}` }}>
            <span style={{ color: C.muted }}>Mould temperature</span>
            <span style={{ fontWeight: '700', color: C.teal }}>{OPT_PARAMS.mould_temp}°C</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0' }}>
            <span style={{ color: C.muted }}>Est. melt temp</span>
            <span style={{ fontWeight: '700', color: C.teal }}>{OPT_PARAMS.melt_temp_estimated}°C</span>
          </div>
        </div>

        {/* Injection */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.08em', marginBottom: '12px' }}>Injection</div>
          {[['Fill time', `${OPT_PARAMS.fill_time_s}s`, C.teal], ['Max inj pressure', `${OPT_PARAMS.inj_pressure_bar} bar`, C.teal], ['V→P', '38.2 mm', C.teal]].map(([l, v, c]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.muted }}>{l}</span><span style={{ fontWeight: '700', color: c }}>{v}</span>
            </div>
          ))}
          {OPT_PARAMS.inj_stages.map((st, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '3px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.muted }}>S{i + 1}</span>
              <span style={{ color: C.text }}>{st.speed_pct}% · {st.pressure_bar} bar</span>
            </div>
          ))}
        </div>

        {/* Hold / Pack */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.08em', marginBottom: '12px' }}>Hold / Pack</div>
          {OPT_PARAMS.hold_stages.map((h, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '3px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.muted }}>H{i + 1}</span>
              <span style={{ color: C.text }}>{h.pressure_bar} bar · {h.time_s}s</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', marginTop: '4px' }}>
            <span style={{ color: C.muted }}>Gate freeze</span><span style={{ fontWeight: '700', color: C.amber }}>{OPT_PARAMS.gate_freeze_s}s</span>
          </div>
        </div>

        {/* Cooling */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: C.muted, letterSpacing: '0.08em', marginBottom: '12px' }}>Cooling & Plasticising</div>
          {[['Min cooling', `${OPT_PARAMS.cooling_min_s}s`, C.muted], ['Rec. cooling', `${OPT_PARAMS.cooling_rec_s}s`, C.teal], ['Recovery time', `${OPT_PARAMS.recovery_time_s}s`, C.text], ['Screw speed', `${OPT_PARAMS.screw_speed_rpm} rpm`, C.teal], ['Back pressure', `${OPT_PARAMS.back_pressure_bar} bar`, C.teal]].map(([l, v, c]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.muted }}>{l}</span><span style={{ fontWeight: '700', color: c }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Version history strip */}
      <SectionLabel>Version History</SectionLabel>
      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto' }}>
        {[
          { v: 1, type: 'calculated', label: 'Initial calc', date: '12 May' },
          { v: 2, type: 'edit',       label: 'Tuned zones',  date: '14 May' },
          { v: 3, type: 'troubleshoot', label: 'Hold adj.',  date: '16 May' },
          { v: 4, type: 'optimise',   label: 'Optimise',     date: '19 May', current: true },
        ].map(ver => (
          <div key={ver.v} style={{ background: ver.current ? C.tealDim : C.bg, border: `1px solid ${ver.current ? C.teal : C.border}`, borderRadius: '8px', padding: '8px 14px', flexShrink: 0, minWidth: '110px' }}>
            <div style={{ fontSize: '10px', color: ver.current ? C.teal : C.muted, fontWeight: '700', marginBottom: '3px' }}>v{ver.v} · {ver.type}</div>
            <div style={{ fontSize: '11px', color: C.text, fontWeight: '600' }}>{ver.label}</div>
            <div style={{ fontSize: '10px', color: C.muted, marginTop: '2px' }}>{ver.date}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main preview page ─────────────────────────────────────────
export default function Preview() {
  const [section, setSection] = useState('today');

  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text, fontFamily: F }}>
      <style>{`
        @keyframes optimise-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(147,197,253,0); }
          50% { box-shadow: 0 0 12px 3px rgba(147,197,253,0.35); }
        }
        * { box-sizing: border-box; }
        input { outline: none; }
        button { outline: none; }
      `}</style>

      <SectionNav active={section} setActive={setSection} />

      {section === 'today'    && <TodayPreview />}
      {section === 'schedule' && <SchedulePreview />}
      {section === 'live'     && <LivePanelPreview />}
      {section === 'modals'   && <ModalsPreview />}
      {section === 'optim'    && <OptimisationPreview />}
    </div>
  );
}
